import { Form } from '@livecontrol/core-ui';
import { Event } from '@livecontrol/scheduler/model';
import cx from 'classnames';
import { FormikProvider } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import type { SimpleInterpolation} from 'styled-components';
import styled, { css } from 'styled-components';
import type { ScheduledEvent } from '../../graphql';
import { DefconList } from './defcon-list';
import { useForm } from './form';
import { Stream } from './stream';
import Phase = Event.Phase;

const Layout = styled.div<{ fullLayout: boolean }>`
display: grid;
grid-template-columns: 1fr;
grid-template-rows: ${({ fullLayout }): string => fullLayout ? 'repeat(2, 1fr min-content) auto' : '1fr min-content 1fr'};

.x-viewport-lg & {
    grid-template-columns: ${({ fullLayout }): string => fullLayout ? '1fr min-content 200px min-content 180px' : '1fr min-content 200px'};
    grid-template-rows: 1fr
}

.x-viewport-xl & {
    grid-template-columns: ${({ fullLayout }): string => fullLayout ? '1fr min-content 200px min-content 400px' : '1fr min-content 200px'};
    grid-template-rows: 1fr
}

.text-success {
    color: #69d7ba;
}

.form-control:disabled {
    background-color: var(--white10);
}
`;

const Separator = styled.div<{ direction?: 'bottom' | 'left' | 'right' | 'top' }>`
  ${({ direction = 'left' }): SimpleInterpolation | undefined =>
     css`
          border-${direction}: 1px solid var(--white40);
        `}
      display: grid;
`;

export const Webplayer = ({ scheduledEvent }: { scheduledEvent: ScheduledEvent }): React.ReactElement => {
    const [enabledRedirect, setEnabledRedirect] = useState(false);
    const [showSaved, setShowSaved] = useState(false);
    const [fieldSelected, setFieldSelected] = useState('');
    const { formik, loading, error, event } = useForm({ scheduledEvent });
    const [bannerMessage, setBannerMessage] = useState<string>();

    const setBannerDefault = useCallback((message?: string) => (): void => {
        formik.setFieldValue('banner', message);
        setBannerMessage(message);
    }, [formik.setFieldValue]);

    const predefinedMessages = [
        undefined,
        "Our team is aware of the audio issue and we're actively working to resolve it. Please standby!",
        "Our team is aware of the video issue and we're actively working to resolve it. Please standby!",
        'This service has lost cellular coverage. This video will be available for download after completion of the service.',
        'Our team is aware that there is a technical issue.  We are working actively to resolve it.'
    ];

    useEffect(() => {
        if (!error && !loading && event) {
            setTimeout(() => {
                setShowSaved(false);
            }, 5000);

            setShowSaved(true);
            setEnabledRedirect(false);
          // setEnabledRedirect(false);
        }
      }, [error, loading, event]);

    const {
        status,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        start_time,
        location
    } = scheduledEvent;

    const webplayerClientId = location?.user?.id;
    const isLive = status === Phase.Live.toString();
    const isUpcoming = DateTime.fromISO(start_time) > DateTime.now();
    const showDefcon = isUpcoming || isLive;

    const handleClick = (field: string) => (): void => {
        setFieldSelected(field);
        void formik.submitForm();
    };

    return (
        <Layout fullLayout={showDefcon} className='text-white gap-16px mt-24px'>
            <FormikProvider value={formik}>
                <Form className='d-flex flex-column'>

                    <div className='d-flex justify-content-between'>
                        <h2 className='text-white font-size-18px'>Redirect URL</h2>
                        <span className='text-success' style={{ opacity: (!error && showSaved && fieldSelected === 'redirectURL') ? 1 : 0 }}><i className='fas fa-check mr-8px' />Saved</span>
                    </div>
                    <div className='d-flex gap-16px'>
                        <Form.Control name='redirectURL' disabled={!enabledRedirect}
                            value={formik.values.redirectURL}
                            onChange={(e): void => {
                                formik.setFieldValue('redirectURL', e.target.value);
                            }}
                        />
                        {enabledRedirect ? (
                            <button type='button' className={cx('btn btn-primary')}
                                disabled={loading}
                                onClick={handleClick('redirectURL')}
                            >
                            Save</button>
                        ) : (
                        <button type='button' disabled={loading} className={cx('btn btn-primary')}
                        onClick={(): void => {
                        setEnabledRedirect(true);
                        }}>Edit</button>
                        )}
                        <button disabled={!enabledRedirect || loading} type='button' className={cx('btn btn-outline-secondary')}
                        onClick={(): void => {
                            setEnabledRedirect(false);
                        }}>
                            Cancel
                        </button>
                    </div>

                    <h2 className='mt-24px font-size-18px'>Webplayer Banner</h2>
                    <div className='d-flex justify-content-between'>
                        <small className='text-white60'>This text will be displayed to every viewer when saved. <br/>
                        Please double-check for content, spelling and grammar.</small>
                        <span className='text-success align-self-end text-nowrap' style={{ opacity: (!error && showSaved && fieldSelected === 'banner') ? 1 : 0 }}><i className='fas fa-check mr-8px' />Saved</span>
                    </div>
                    <div className='d-flex gap-16px align-items-center mt-8px'>
                        <Form.Control as='textarea' name='banner'
                        disabled={!!scheduledEvent.banner || loading}
                        value={formik.values.banner}
                        onChange={(e): void => {
                            formik.setFieldValue('banner', e.target.value);
                        }}
                        />
                        <div className='d-flex gap-16px'>
                            <button type='button' className='btn btn-primary text-wrap'
                                disabled={!!scheduledEvent.banner || loading}
                                onClick={handleClick('banner')}
                            >Push to Webplayer</button>
                            <button type='button' className='btn btn-outline btn-outline-light'
                                disabled={loading}
                                onClick={(): void => {
                                    formik.setFieldValue('banner', '');
                                    handleClick('banner')();
                                }}
                            >Clear</button>
                        </div>
                    </div>
                    <Dropdown className="mt-12px">
                        <Dropdown.Toggle variant='outline-lightgrey' disabled={!!scheduledEvent.banner || loading}>
                            Predefined Messages
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {
                                predefinedMessages.map((message, i) => (
                                    <Dropdown.Item key={message ?? (i + 1)}
                                    active={bannerMessage === message}
                                    onSelect={setBannerDefault(message)}
                                    >
                                        {message ?? '(Blank)'}
                                    </Dropdown.Item>
                                ))
                            }
                        </Dropdown.Menu>
                    </Dropdown>
                </Form>
            </FormikProvider>
            <Separator className='d-none d-lg-grid' />
            <Separator direction='top' className='d-grid d-lg-none' />
            <Stream scheduledEvent={scheduledEvent} />
            {showDefcon && (
            <>
            <Separator className='d-none d-lg-grid' />
            <Separator direction='top' className='d-grid d-lg-none' />
            <DefconList webplayerClientId={webplayerClientId} />
            </>
            )}
        </Layout>
    );
};
